import React from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import orderBy from "lodash/orderBy";
import { Manager, Reference, Popper } from "react-popper";
import AutoWhatever from "react-autowhatever";
import Highlighter from "react-highlight-words";
import IsolatedScroll from "react-isolated-scroll";
import Input from "reactstrap/lib/Input";

import Icon from "../icon/icon";
import Label from "../label/label";
import { Search, MenuClose } from "../../lib/icons";
import { gatherClasses, isFunction, isDescendant } from "../../lib/utils";
import defaultTheme from "./theme";
import "./autoSuggest.scss";

class AutoSuggest extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      data: !props.async ? props.data : [],
      inputValue: props.defaultValue,
      inputTouched: false,
      filteredItems: props.async ? props.data : [],
      highlightedSectionIndex: null,
      highlightedItemIndex: null,
      popperKey: "INITIALIZE"
    };
    this.inputRef = React.createRef();
  }

  // TODO Fix this recreate of popper in case of modal
  // because the position of auto suggest differs between
  // modal transition start and end
  componentDidMount /* istanbul ignore next */() {
    const modal = document.querySelector(".oui-modal");
    if (modal !== null && isDescendant(modal, this.inputRef.current)) {
      setTimeout(() => {
        this.setState({
          popperKey: "RECREATE"
        });
      }, 1000);
    }
  }

  static getDerivedStateFromProps(props) {
    const { data, async } = props;
    const state = async ? { filteredItems: data } : { data };
    return state;
  }

  onChange = event => {
    const { minChars, onChange } = this.props;
    const { value } = event.target;
    const minChar = Math.max(0, minChars);
    let filteredItems = [];

    if (value.length >= minChar) {
      filteredItems = this.filterItems(value);
    }

    this.setState(
      {
        filteredItems,
        inputValue: value,
        inputTouched: true,
        highlightedSectionIndex: null,
        highlightedItemIndex: null
      },
      () => {
        if (isFunction(onChange)) {
          onChange(value);
        }
      }
    );
  };

  onAsycChange = event => {
    const { minChars, onChange } = this.props;
    const { value } = event.target;
    const minChar = Math.max(0, minChars);
    const shouldFireChange = value.length >= minChar;

    this.setState(
      {
        inputValue: value,
        inputTouched: true
      },
      () => {
        if (isFunction(onChange) && shouldFireChange) {
          onChange(value);
        }
      }
    );
  };

  onKeyDown = (
    event,
    {
      highlightedSectionIndex,
      highlightedItemIndex,
      newHighlightedSectionIndex,
      newHighlightedItemIndex
    }
  ) => {
    const { onItemSelect, onEnter } = this.props;
    const { inputValue } = this.state;

    const newItem = this.getItemBySectionAndItemIndex(
      newHighlightedSectionIndex,
      newHighlightedItemIndex
    );
    const newInputValue = newItem
      ? this.getInputValueOnSelect(newItem)
      : inputValue;

    const selectedItem = this.getItemBySectionAndItemIndex(
      highlightedSectionIndex,
      highlightedItemIndex
    );
    const selectedInputValue = selectedItem
      ? this.getInputValueOnSelect(selectedItem)
      : inputValue;

    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
        event.preventDefault();
        this.setState({
          highlightedSectionIndex: newHighlightedSectionIndex,
          highlightedItemIndex: newHighlightedItemIndex,
          inputValue: newInputValue
        });
        break;

      case "Escape":
        this.setState({
          highlightedSectionIndex: null,
          highlightedItemIndex: null,
          filteredItems: [],
          inputTouched: false
        });
        break;

      case "Enter":
        if (highlightedItemIndex !== null) {
          this.setState(
            {
              inputValue: selectedInputValue,
              filteredItems: [],
              highlightedSectionIndex: null,
              highlightedItemIndex: null,
              inputTouched: false
            },
            () => {
              if (isFunction(onItemSelect)) {
                onItemSelect(selectedItem);
              }
            }
          );
        } else {
          this.setState(
            {
              filteredItems: [],
              highlightedSectionIndex: null,
              highlightedItemIndex: null,
              inputTouched: false
            },
            () => {
              const { inputValue: plainInputValue } = this.state;
              if (isFunction(onEnter)) {
                onEnter(plainInputValue);
              }
            }
          );
        }
        break;
      default:
        break;
    }
  };

  onFocus = event => {
    const { onFocus } = this.props;
    const { inputValue } = this.state;

    if (isFunction(onFocus)) {
      onFocus(event, inputValue);
    }
  };

  onBlur = event => {
    const { inputValue, highlightedItemIndex: prevHighlightedItemIndex } =
      this.state;
    const { onBlur } = this.props;

    this.setState(
      {
        filteredItems: [],
        highlightedSectionIndex: null,
        highlightedItemIndex: null,
        inputTouched: false
      },
      () => {
        if (prevHighlightedItemIndex !== null) {
          this.inputRef.current.focus();
        } else if (isFunction(onBlur)) {
          onBlur(event, inputValue);
        }
      }
    );
  };

  onMouseEnter = (_, { sectionIndex, itemIndex }) => {
    this.setState({
      highlightedSectionIndex: sectionIndex,
      highlightedItemIndex: itemIndex
    });
  };

  onMouseLeave = () => {
    this.setState({
      highlightedSectionIndex: null,
      highlightedItemIndex: null
    });
  };

  onMouseDown = item => {
    const { onItemSelect } = this.props;

    this.setState(
      {
        inputValue: this.getInputValueOnSelect(item)
      },
      () => {
        if (isFunction(onItemSelect)) {
          onItemSelect(item);
        }
      }
    );
  };

  onClearInput = () => {
    const { onClear } = this.props;

    this.setState(
      {
        inputValue: "",
        filteredItems: [],
        highlightedSectionIndex: null,
        highlightedItemIndex: null,
        inputTouched: false
      },
      () => {
        if (isFunction(onClear)) {
          onClear();
        }
      }
    );
  };

  getInputValueOnSelect = item => {
    const { getItemValue } = this.props;
    const inputValue = isFunction(getItemValue)
      ? getItemValue(item)
      : item.label;

    if (typeof inputValue !== "string") {
      throw new Error("Input value must be a string");
    }
    return inputValue;
  };

  getItemBySectionAndItemIndex = (sectionIndex, itemIndex) => {
    const { filteredItems } = this.state;
    const section = filteredItems[sectionIndex];
    if (!section) {
      return null;
    }
    /* istanbul ignore next  */
    const item = section && section.items ? section.items[itemIndex] : section;
    return item;
  };

  getItemProps = ({ itemIndex, sectionIndex }) => ({
    onMouseEnter: this.onMouseEnter,
    onMouseLeave: this.onMouseLeave,
    onMouseDown: ((sIndex, iIndex) => {
      return () => {
        const item = this.getItemBySectionAndItemIndex(sIndex, iIndex);
        this.onMouseDown(item);
      };
    })(sectionIndex, itemIndex)
  });

  getInputProps = () => {
    const { disabled, placeholder, async } = this.props;
    const { inputValue } = this.state;

    return {
      disabled,
      placeholder,
      value: inputValue,
      onChange: async ? this.onAsycChange : this.onChange,
      onKeyDown: this.onKeyDown,
      onFocus: this.onFocus,
      onBlur: this.onBlur
    };
  };

  filterItems = value => {
    const { filterItem } = this.props;
    const { data } = this.state;
    const escapedValue = value.trim();

    const filteredItems = data
      .map(item => {
        const clonedItem = { ...item };
        const isGroup = !!clonedItem.items;
        if (!isGroup) {
          if (
            (isFunction(filterItem) &&
              !!filterItem(escapedValue, clonedItem)) ||
            clonedItem.label.toLowerCase().includes(escapedValue.toLowerCase())
          ) {
            return clonedItem;
          }
        } else {
          const matchingGroupItems = clonedItem.items.filter(groupItem => {
            return (
              (isFunction(filterItem) &&
                !!filterItem(escapedValue, groupItem)) ||
              groupItem.label.toLowerCase().includes(escapedValue.toLowerCase())
            );
          });

          if (matchingGroupItems.length > 0) {
            clonedItem.items = matchingGroupItems;
            return clonedItem;
          }
        }
        return null;
      })
      .filter(Boolean);

    return this.cutDownResultsByLimit(filteredItems);
  };

  cutDownResultsByLimit(results) {
    const { maxResultsLimit } = this.props;
    const reordered = orderBy(results, ["items"]);
    let groupsCount = 0;
    const remapped = reordered.map(item => {
      if (item.items) {
        groupsCount += 1;
        item.items = item.items.slice(0, maxResultsLimit);
      }
      return item;
    });

    return remapped.slice(0, maxResultsLimit + groupsCount);
  }

  renderInputComponent = inputProps => {
    const { ariaLabel } = this.props;
    const { inputValue } = this.state;
    const inputNotEmpty = inputValue.trim().length > 0;

    return (
      <Reference>
        {({ ref }) => (
          <div ref={ref}>
            <div className="oui-search-input-container">
              <Icon src={Search} className="oui-search-input-icon" />
              <Input
                {...inputProps}
                type="text"
                innerRef={this.inputRef}
                className={gatherClasses(
                  null,
                  "oui-search-input-textbox",
                  inputNotEmpty ? "oui-input-search-enabled" : null
                )}
                aria-label={ariaLabel}
              />
              {inputNotEmpty ? (
                <span
                  className="oui-search-input-clear"
                  onClick={this.onClearInput}
                  aria-hidden="true"
                >
                  <Icon src={MenuClose} width="10px" height="10px" clickable />
                </span>
              ) : null}
            </div>
          </div>
        )}
      </Reference>
    );
  };

  renderItemsContainer = ({ children, containerProps }) => {
    const { height, showEmptyMessage } = this.props;
    const { inputValue, inputTouched } = this.state;
    const { ref, ...restContainerProps } = containerProps;
    const callRef = isolatedScroll => {
      if (isolatedScroll !== null) {
        ref(isolatedScroll.component);
      }
    };

    const containerStyle = {
      overflowY: "auto",
      maxHeight: `${height}px`
    };

    return ReactDOM.createPortal(
      <Popper
        modifiers={{
          applyStyles: {
            enabled: true,
            fn: data => /* istanbul ignore next  */ {
              data.styles.width = `${data.offsets.reference.width}px`;
              return data;
            }
          }
        }}
      >
        {({ ref: scrollRef, style }) =>
          showEmptyMessage &&
          children === null &&
          inputValue !== "" &&
          inputTouched ? (
            <div
              ref={scrollRef}
              style={style}
              className="oui-react-autowhatever__items-portal-wrapper"
            >
              <div className="oui-react-autowhatever__items-container oui-react-autowhatever__items-container--empty">
                <Label
                  text="No matching results found"
                  muted
                  className="oui-mb-0"
                />
              </div>
            </div>
          ) : (
            <div
              ref={scrollRef}
              style={style}
              className="oui-react-autowhatever__items-portal-wrapper"
            >
              <IsolatedScroll
                ref={callRef}
                {...restContainerProps}
                style={containerStyle}
              >
                {children}
              </IsolatedScroll>
            </div>
          )
        }
      </Popper>,
      document.querySelector("body")
    );
  };

  renderSectionTitle = section => {
    return section.items ? section.label : null;
  };

  getSectionItems = section => {
    return section.items || [section];
  };

  renderHighlightedText = (value, highlight) => text =>
    highlight ? (
      <Highlighter
        highlightClassName="oui-is-highlighting"
        searchWords={value.split(" ")}
        autoEscape
        textToHighlight={text}
        highlightTag={({ children }) => (
          <span className="oui-is-highlighting">{children}</span>
        )}
      />
    ) : (
      text
    );

  renderItem = (item, { value }) => {
    const { highlightMatches: h, renderMenuItem } = this.props;
    return isFunction(renderMenuItem)
      ? renderMenuItem(item, this.renderHighlightedText(value, h))
      : h
      ? this.renderHighlightedText(value, h)(item.label)
      : item.label;
  };

  render() {
    const { dataTestId } = this.props;
    const {
      popperKey,
      filteredItems,
      inputValue,
      highlightedSectionIndex,
      highlightedItemIndex
    } = this.state;
    return (
      <div
        data-testid={dataTestId}
        className={gatherClasses(this.props, "oui-react-autosuggest")}
      >
        <Manager key={popperKey}>
          <AutoWhatever
            items={filteredItems}
            renderInputComponent={this.renderInputComponent}
            renderItemsContainer={this.renderItemsContainer}
            renderItem={this.renderItem}
            renderItemData={{ value: inputValue }}
            inputProps={this.getInputProps()}
            highlightedSectionIndex={highlightedSectionIndex}
            highlightedItemIndex={highlightedItemIndex}
            itemProps={this.getItemProps}
            multiSection
            renderSectionTitle={this.renderSectionTitle}
            getSectionItems={this.getSectionItems}
            theme={defaultTheme}
          />
        </Manager>
      </div>
    );
  }
}

AutoSuggest.defaultProps = {
  minChars: 1,
  data: [],
  height: 300,
  highlightMatches: false,
  maxResultsLimit: 15,
  defaultValue: "",
  dataTestId: "orion-autosuggest",
  showEmptyMessage: false
};

AutoSuggest.propTypes = {
  defaultValue: PropTypes.string,
  data: PropTypes.array,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  onEnter: PropTypes.func,
  onItemSelect: PropTypes.func,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  renderMenuItem: PropTypes.func,
  getItemValue: PropTypes.func,
  filterItem: PropTypes.func,
  disabled: PropTypes.bool,
  maxResultsLimit: PropTypes.number,
  minChars: PropTypes.number,
  async: PropTypes.bool,
  height: PropTypes.number,
  highlightMatches: PropTypes.bool,
  dataTestId: PropTypes.string,
  ariaLabel: PropTypes.string,
  showEmptyMessage: PropTypes.bool
};

AutoSuggest.displayName = "OrionAutoSuggest";

export default AutoSuggest;
